/* jshint esversion: 6 */

/*
 * The one place that knows how the backend names and shapes link page settings
 * (Linkpage::settingslist_api() and settings_api()). The reducer stores the UI
 * shape and the screen reads it, so a rename on the PHP side is an edit here.
 *
 * UI shape, as stored at `state.linkpages[socialId]`:
 *   enabled     bool    the page is published
 *   url         string  the public URL, assigned by the backend (read only)
 *   title       string  shown at the top of the page; empty means the account name
 *   website     string  the owner's own site, linked from the page; empty means none
 *   itemsShown  int     how many items the page keeps
 */

const FIELDS = {
    enabled: {api: 'activeStatus', fromApi: (value) => Boolean(Number(value)), toApi: (value) => (value ? 1 : 0)},
    url: {api: 'publicUrl', fromApi: (value) => String(value ?? ''), toApi: null},
    title: {api: 'pageTitle', fromApi: (value) => String(value ?? ''), toApi: (value) => value.trim()},
    website: {api: 'websiteURL', fromApi: (value) => String(value ?? ''), toApi: (value) => value.trim()},
    itemsShown: {api: 'itemsOnPage', fromApi: (value) => parseInt(value, 10) || 0, toApi: (value) => value}
};

const API_TO_UI = Object.keys(FIELDS).reduce((map, key) => {
    map[FIELDS[key].api] = key;
    return map;
}, {});

/**
 * Only the keys the payload carries, so a partial echo from an update can be
 * merged over the stored record without blanking the fields it left out.
 */
export function fromLinkpageApi(payload) {
    const settings = {};

    if (!payload || typeof payload !== 'object') {
        return settings;
    }

    Object.keys(FIELDS).forEach((key) => {
        if (payload[FIELDS[key].api] !== undefined) {
            settings[key] = FIELDS[key].fromApi(payload[FIELDS[key].api]);
        }
    });

    return settings;
}

export function toLinkpageApi(changes) {
    const body = {};

    Object.keys(changes).forEach((key) => {
        if (FIELDS[key] && FIELDS[key].toApi) {
            body[FIELDS[key].api] = FIELDS[key].toApi(changes[key]);
        }
    });

    return body;
}

function firstMessage(value) {
    if (Array.isArray(value)) {
        return firstMessage(value[0]);
    }

    return (typeof value === 'string' && value.length) ? value : null;
}

/**
 * Turns the api error (as `getApiErrors` returns it) into what the screen draws.
 *
 * Validation messages keyed by a field we show go under that field; anything
 * else is page-level. A failure with no usable message still has to say
 * something, so it falls back to `fallback`.
 *
 * A network failure gets the fallback too, not a message about the network:
 * the app reducer already toasts "Network error" for every request that never
 * got an answer, so this says what didn't happen and the toast says why.
 *
 * @return {{formError: ?string, fieldErrors: Object<string, string>}}
 */
export function linkpageErrorMessages(apiError, fallback) {
    const result = {formError: null, fieldErrors: {}};

    if (!apiError) {
        return result;
    }

    const validation = (apiError.errors && apiError.errors.validation) || {};
    const pageLevel = [];

    Object.keys(validation).forEach((apiName) => {
        const message = firstMessage(validation[apiName]);
        if (!message) {
            return;
        }
        if (API_TO_UI[apiName] !== undefined) {
            result.fieldErrors[API_TO_UI[apiName]] = message;
        } else {
            pageLevel.push(message);
        }
    });

    if (pageLevel.length) {
        result.formError = pageLevel.join(' ');
    } else if (Object.keys(result.fieldErrors).length === 0) {
        result.formError = fallback;
    }

    return result;
}
